// auth/AuthContext.tsx
import React, { createContext, useContext, useReducer, useEffect, ReactNode } from 'react';
import liffService, { UserRegistrationState } from '../services/liff';
import { UserRegistrationData } from '../components/UserRegistrationPopup';

// 認証状態の型定義（ユーザーIDのみ）
export interface AuthState {
  userId: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  needsRegistration: boolean;
  needsStoreRegistration: boolean;
  // 退会済みアカウントでの再入場（080）。店舗登録ダイアログではなく再開ダイアログを出す
  needsReactivation: boolean;
  pendingStoreName: string | null;
  error: string | null;
}

// アクションの型定義
type AuthAction =
  | { type: 'SET_LOADING'; payload: boolean }
  | { type: 'SET_USER_ID'; payload: string }
  | { type: 'SET_NEEDS_REGISTRATION'; payload: boolean }
  | { type: 'SET_NEEDS_STORE_REGISTRATION'; payload: { storeName: string } }
  | { type: 'CLEAR_STORE_REGISTRATION' }
  | { type: 'SET_NEEDS_REACTIVATION'; payload: { storeName: string } }
  | { type: 'CLEAR_REACTIVATION' }
  | { type: 'SET_ERROR'; payload: string | null }
  | { type: 'LOGOUT' }
  | { type: 'CLEAR_ERROR' };

// 初期状態
const initialState: AuthState = {
  userId: null,
  isAuthenticated: false,
  isLoading: true,
  needsRegistration: false,
  needsStoreRegistration: false,
  needsReactivation: false,
  pendingStoreName: null,
  error: null,
};

// レデューサー
const authReducer = (state: AuthState, action: AuthAction): AuthState => {
  switch (action.type) {
    case 'SET_LOADING':
      return {
        ...state,
        isLoading: action.payload,
      };
    case 'SET_USER_ID':
      return {
        ...state,
        userId: action.payload,
        isAuthenticated: true,
        needsRegistration: false,
        // 再開・店舗登録の完了はここを通るため、残っているダイアログ要求を必ず落とす
        needsReactivation: false,
        needsStoreRegistration: false,
        isLoading: false,
        error: null,
      };
    case 'SET_NEEDS_REGISTRATION':
      return {
        ...state,
        needsRegistration: action.payload,
        isAuthenticated: false,
        isLoading: false,
      };
    case 'SET_NEEDS_STORE_REGISTRATION':
      return {
        ...state,
        needsStoreRegistration: true,
        pendingStoreName: action.payload.storeName,
        isAuthenticated: false,
        isLoading: false,
      };
    case 'CLEAR_STORE_REGISTRATION':
      return {
        ...state,
        needsStoreRegistration: false,
        pendingStoreName: null,
      };
    case 'SET_NEEDS_REACTIVATION':
      return {
        ...state,
        needsReactivation: true,
        // 退会は全店舗の所属を無効化するため、店舗登録ダイアログとは排他にする
        needsStoreRegistration: false,
        pendingStoreName: action.payload.storeName,
        isAuthenticated: false,
        isLoading: false,
      };
    case 'CLEAR_REACTIVATION':
      return {
        ...state,
        needsReactivation: false,
        pendingStoreName: null,
      };
    case 'SET_ERROR':
      return {
        ...state,
        error: action.payload,
        isLoading: false,
      };
    case 'LOGOUT':
      return {
        ...initialState,
        isLoading: false,
      };
    case 'CLEAR_ERROR':
      return {
        ...state,
        error: null,
      };
    default:
      return state;
  }
};

// AuthContext の型定義
interface AuthContextType {
  // 状態
  userId: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  needsRegistration: boolean;
  needsStoreRegistration: boolean;
  needsReactivation: boolean;
  pendingStoreName: string | null;
  error: string | null;

  // メソッド
  login: () => Promise<void>;
  logout: () => void;
  registerUser: (userData: UserRegistrationData) => Promise<{ success: boolean; error?: string }>;
  registerStore: () => Promise<{ success: boolean; error?: string }>;
  cancelStoreRegistration: () => void;
  reactivateAccount: () => Promise<{ success: boolean; error?: string }>;
  cancelReactivation: () => void;
  clearError: () => void;
  initialize: () => Promise<void>;
}

// Context作成
const AuthContext = createContext<AuthContextType | undefined>(undefined);

// AuthProvider Props
interface AuthProviderProps {
  children: ReactNode;
}

// AuthProvider コンポーネント
export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [state, dispatch] = useReducer(authReducer, initialState);

  // 初期化処理
  const initialize = async () => {
    dispatch({ type: 'SET_LOADING', payload: true });
    
    try {
      // liffService のコールバックを設定
      liffService.setAuthCallbacks?.({
        onUserAuthenticated: (userId: string) => {
          dispatch({ type: 'SET_USER_ID', payload: userId });
        },
        onRegistrationRequired: (state: UserRegistrationState) => {
          dispatch({ type: 'SET_NEEDS_REGISTRATION', payload: state.needsRegistration });
        },
        onStoreRegistrationRequired: (storeName: string, _userId: string) => {
          dispatch({ type: 'SET_NEEDS_STORE_REGISTRATION', payload: { storeName } });
        },
        onReactivationRequired: (storeName: string, _userId: string) => {
          dispatch({ type: 'SET_NEEDS_REACTIVATION', payload: { storeName } });
        }
      });

      // liffService を初期化
      const success = await liffService.initialize();
      if (!success) {
        throw new Error('LIFF初期化に失敗しました');
      }

    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : '初期化エラーが発生しました';
      
      // Log error to console for debugging
      console.error('[AuthContext] Initialization error:', errorMessage);
      
      dispatch({ 
        type: 'SET_ERROR', 
        payload: errorMessage
      });
    }
  };

  // ログイン処理
  const login = async () => {
    dispatch({ type: 'SET_LOADING', payload: true });
    
    try {
      await liffService.login();
    } catch (error) {
      dispatch({ 
        type: 'SET_ERROR', 
        payload: error instanceof Error ? error.message : 'ログインエラーが発生しました' 
      });
    }
  };

  // ログアウト処理
  const logout = () => {
    liffService.logout();
    dispatch({ type: 'LOGOUT' });
  };

  // ユーザー登録処理
  // registerStore / reactivateAccount と同様に SET_LOADING / SET_ERROR は使わない。
  // SET_LOADING は App の全画面ローディングで登録ポップアップをアンマウントし、入力途中の
  // フォームごと消してしまう。SET_ERROR は全画面「初期化エラー」に飛ばすため、
  // 「このメールアドレスは既に登録されています」のような入力の直し方が分かる案内が
  // 初期化エラーとして出て、しかも入力をやり直すことになる。
  // 進捗表示と失敗理由の提示はポップアップ側（isSubmitting / ErrorAlert）が担う。
  const registerUser = async (userData: UserRegistrationData): Promise<{ success: boolean; error?: string }> => {
    try {
      const result = await liffService.registerUser(userData);

      if (result.success) {
        // 登録成功時は、liffServiceのコールバックでユーザー情報が設定される
        dispatch({ type: 'SET_NEEDS_REGISTRATION', payload: false });
        return { success: true };
      }
      return { success: false, error: result.error || 'ユーザー登録に失敗しました' };
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'ユーザー登録中にエラーが発生しました';
      return { success: false, error: errorMessage };
    }
  };

  // エラークリア
  const clearError = () => {
    dispatch({ type: 'CLEAR_ERROR' });
  };

  // 店舗追加登録処理
  // 利用再開（reactivateAccount）と同じ理由で SET_LOADING / SET_ERROR を使わない。
  // SET_LOADING は App の全画面ローディングでダイアログをアンマウントさせ、SET_ERROR は
  // 全画面「初期化エラー」画面に飛ばしてしまう（店舗登録の失敗は初期化エラーではない。
  // 例: by-relation の 400「既に登録されています」）。StoreRegistrationDialog は
  // 戻り値の error をインライン表示できるので、失敗の理由はダイアログ内に出す。
  const registerStore = async (): Promise<{ success: boolean; error?: string }> => {
    try {
      const result = await liffService.addStoreRegistration();

      if (result.success) {
        dispatch({ type: 'CLEAR_STORE_REGISTRATION' });
        return { success: true };
      }
      return { success: false, error: result.error || '店舗登録に失敗しました' };
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : '店舗登録中にエラーが発生しました';
      return { success: false, error: errorMessage };
    }
  };

  // 店舗登録キャンセル
  const cancelStoreRegistration = () => {
    dispatch({ type: 'CLEAR_STORE_REGISTRATION' });
  };

  // 退会アカウントの利用再開（080）
  // ここで SET_LOADING を立てないのは意図的。App が isLoading で全画面ローディングに
  // 差し替えるとダイアログ自体がアンマウントされ、失敗時に理由を表示できないため
  // （進捗表示はダイアログ内のスピナーが担う）。
  const reactivateAccount = async (): Promise<{ success: boolean; error?: string }> => {
    try {
      const result = await liffService.reactivateAccount();

      if (result.success) {
        // 成功時は liffService のコールバック（onUserAuthenticated）で認証済みになる
        dispatch({ type: 'CLEAR_REACTIVATION' });
        return { success: true };
      }
      // 画面全体をエラー表示に切り替えず、ダイアログ内に理由を出す
      return { success: false, error: result.error };
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : '利用の再開中にエラーが発生しました';
      return { success: false, error: errorMessage };
    }
  };

  // 利用再開のキャンセル（退会したままにする）
  const cancelReactivation = () => {
    dispatch({ type: 'CLEAR_REACTIVATION' });
  };

  // 初期化を一度だけ実行
  useEffect(() => {
    initialize();
  }, []);

  const value: AuthContextType = {
    // 状態
    userId: state.userId,
    isAuthenticated: state.isAuthenticated,
    isLoading: state.isLoading,
    needsRegistration: state.needsRegistration,
    needsStoreRegistration: state.needsStoreRegistration,
    needsReactivation: state.needsReactivation,
    pendingStoreName: state.pendingStoreName,
    error: state.error,

    // メソッド
    login,
    logout,
    registerUser,
    registerStore,
    cancelStoreRegistration,
    reactivateAccount,
    cancelReactivation,
    clearError,
    initialize,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

// useAuth カスタムフック
export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export default AuthContext;